import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';

import enTranslations from './locales/en.json';
import { workflowTranslationKeys, COMMON, WORKFLOWAUTOMATION, SCHEDULER } from './workflowNamespaces';

// Supported languages
export const supportedLanguages = [
  'en', 'es', 'fr', 'de', 'ar',
  'it', 'pt', 'ru', 'zh-CN', 'zh-TW',
  'ja', 'ko', 'hi', 'bn', 'ur',
  'tr', 'nl', 'pl', 'sv', 'da',
  'fi', 'no', 'el', 'cs', 'id', 'mr',
];

/**
 * Normalizes language code and validates support.
 * Examples: "en-US" -> "en", "AR" -> "ar"
 * @param {string} lang
 * @returns {string|null}
 */
export const normalizeLanguage = (lang) => {
  if (!lang || typeof lang !== "string") {
    return null;
  }

  // Check full code first (handles zh-CN, zh-TW, mr, etc.)
  if (supportedLanguages.includes(lang)) return lang;

  // Try lowercased full code
  const lower = lang.toLowerCase();
  if (supportedLanguages.includes(lower)) return lower;

  // Try base language code (e.g. 'en-US' -> 'en')
  const base = lower.split("-")[0];
  return supportedLanguages.includes(base) ? base : null;
};

/**
 * Detects browser language and maps it to a supported language
 * @returns {string} Language code (en, es, fr, de, ar)
 */
const detectBrowserLanguage = () => {
  // Get browser language(s)
  const browserLang = navigator.language || navigator.userLanguage;
  const browserLangs = navigator.languages || [browserLang];

  // Extract base language code (e.g., 'en-US' -> 'en', 'es-MX' -> 'es')
  const getBaseLanguage = (lang) => normalizeLanguage(lang);

  // Check if browser language is supported
  for (const lang of browserLangs) {
    const baseLang = getBaseLanguage(lang);
    if (baseLang) {
      return baseLang;
    }
  }

  // Check primary browser language
  const primaryBaseLang = getBaseLanguage(browserLang);
  if (primaryBaseLang) {
    return primaryBaseLang;
  }

  // Default to English if browser language is not supported
  return 'en';
};

// Get saved language from localStorage, or detect browser language, or default to 'en'
const savedLanguage = localStorage.getItem('i18nextLng') || detectBrowserLanguage();

i18n
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    // Only English is bundled synchronously as the guaranteed fallback.
    // All other locales are fetched on demand via HttpBackend.
    resources: {
      en: {
        translation: { ...enTranslations, ...workflowTranslationKeys },
        COMMON,
        WORKFLOWAUTOMATION,
        SCHEDULER,
      },
    },
    ns: ['translation', 'COMMON', 'WORKFLOWAUTOMATION', 'SCHEDULER'],
    defaultNS: 'translation',
    partialBundledLanguages: true,
    backend: {
      loadPath: `${process.env.PUBLIC_URL}/locales/{{lng}}.json`,
    },
    lng: savedLanguage,
    fallbackLng: 'en',
    debug: false,
    interpolation: {
      escapeValue: false,
    },
    react: {
      // Prevent Suspense throws during async locale fetch; English fallback
      // renders instantly while the non-English bundle loads in the background.
      useSuspense: false,
    },
  });

// RTL languages
const rtlLanguages = ['ar', 'he', 'fa', 'ur'];

/**
 * Checks if a language is RTL
 * @param {string} lang - Language code
 * @returns {boolean} True if RTL, false otherwise
 */
export const isRTL = (lang) => {
  return rtlLanguages.includes(lang);
};

/**
 * Sets document direction based on language
 * @param {string} lang - Language code
 */
export const setDocumentDirection = (lang) => {
  const direction = isRTL(lang) ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('dir', direction);
  document.documentElement.setAttribute('lang', lang);
};

// Set initial direction
setDocumentDirection(savedLanguage);

// Patch react-select defaultProps once so every Select/CreatableSelect in the
// app gets translated placeholder and noOptionsMessage without touching each file.
const patchReactSelectDefaults = () => {
  const patch = (Component) => {
    if (!Component) return;
    Component.defaultProps = {
      ...Component.defaultProps,
      placeholder: i18n.t('common.selectPlaceholder'),
      noOptionsMessage: () => i18n.t('common.noOptions'),
    };
  };
  // Lazy imports to avoid circular deps at init time
  import('react-select').then((m) => patch(m.default));
  import('react-select/creatable').then((m) => patch(m.default));
};

i18n.on('initialized', patchReactSelectDefaults);

// Save language preference to localStorage when changed and update direction.
// Also persist to backend (lazy import avoids circular dependency at init time).
let _previousLng = savedLanguage;
i18n.on('languageChanged', (lng) => {
  patchReactSelectDefaults();
  const prev = _previousLng;
  _previousLng = lng;
  localStorage.setItem('i18nextLng', lng);
  setDocumentDirection(lng);

  import('../state/store').then(({ store }) => {
    import('../state/middleware/languageSync').then(({ syncLanguageToBackend }) => {
      syncLanguageToBackend(store, lng, prev, i18n);
    });
  });
});

export default i18n;
